import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { seedAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LangSwitch from '../components/LangSwitch';
import { Lock, User, ArrowRight, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const { login, isAuthenticated, isAdmin, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      if (isAdmin) {
        navigate('/admin');
      } else if (user?.client_id) {
        navigate(`/report/${user.client_id}`);
      }
    }
  }, [isAuthenticated, isAdmin, navigate, user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(username, password);
      toast.success(`${t('login_welcome')}, ${u.username}`);
      if (u.role === 'admin') {
        navigate('/admin');
      } else if (u.client_id) {
        navigate(`/report/${u.client_id}`);
      } else {
        toast.error(t('login_no_client'));
      }
    } catch {
      toast.error(t('login_invalid'));
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await seedAPI.seed();
      toast.success(res.data.message);
    } catch {
      toast.error('Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0B0C15 0%, #151621 50%, #1E1F2E 100%)' }}>
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1639152201720-5e536d254d81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjBibG9ja2NoYWluJTIwdGVjaG5vbG9neSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzcyODE1MzE4fDA&ixlib=rb-4.1.0&q=85)`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative z-10 px-16 max-w-lg">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-lg bg-[#7C3AED] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight text-white">ALGO PREMIUM</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6 leading-tight whitespace-pre-line">
            {t('login_hero_title')}
          </h1>
          <p className="text-[#94A3B8] text-base leading-relaxed mb-10">
            {t('login_hero_desc')}
          </p>
          <div className="flex items-center gap-3 text-sm text-[#64748B]">
            <span>{t('login_powered')}</span>
            <span className="text-[#0EA5E9] font-semibold">Strategy Intelligence Solutions</span>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8"
        style={{ background: '#0B0C15' }}>
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-10">
            <div className="lg:hidden flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight text-white">ALGO PREMIUM</span>
            </div>
            <LangSwitch />
          </div>

          <h2 className="text-2xl font-bold tracking-tight text-white mb-2">{t('login_title')}</h2>
          <p className="text-[#94A3B8] text-sm mb-8">{t('login_subtitle')}</p>

          <Card className="bg-[#151621] border-[#2D2E3D] shadow-lg">
            <CardContent className="p-6">
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="text-sm text-[#94A3B8] mb-2 block">{t('login_username')}</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                    <Input
                      data-testid="login-username-input"
                      className="pl-10 bg-[#1E1F2E] border-[#2D2E3D] text-white placeholder:text-[#64748B] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 h-11"
                      placeholder={t('login_username_placeholder')}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-2 block">{t('login_password')}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                    <Input
                      data-testid="login-password-input"
                      type="password"
                      className="pl-10 bg-[#1E1F2E] border-[#2D2E3D] text-white placeholder:text-[#64748B] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 h-11"
                      placeholder={t('login_password_placeholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <Button
                  data-testid="login-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold shadow-[0_0_15px_rgba(124,58,237,0.3)] transition-all duration-300"
                >
                  {loading ? t('login_connecting') : t('login_submit')}
                  {!loading && <ArrowRight className="w-4 h-4 ml-1" />}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-6 text-center">
            <Button
              data-testid="seed-button"
              variant="ghost"
              size="sm"
              className="text-[#64748B] hover:text-[#94A3B8] text-xs"
              onClick={handleSeed}
              disabled={seeding}
            >
              {seeding ? t('login_seeding') : t('login_seed')}
            </Button>
          </div>

          <div className="mt-8 text-center text-xs text-[#64748B]">
            Strategy Intelligence Solutions &middot; si-solutions.ai
          </div>
        </div>
      </div>
    </div>
  );
}
